<template>
  <div class="datev-report-page">
    <div class="page-header">
      <h1>DATEV Report</h1>
      <div class="header-controls">
        <label for="month-input" class="control-label">Month</label>
        <input
          id="month-input"
          v-model="selectedMonth"
          type="month"
          class="month-input"
          :disabled="loading || exportingCsv"
        />
        <button class="btn btn-primary" @click="loadReport" :disabled="loading || exportingCsv">
          {{ loading ? 'Loading...' : 'Load Report' }}
        </button>
        <button
          class="btn btn-secondary"
          @click="downloadDatevZip"
          :disabled="loading || exportingCsv"
        >
          {{ exportButtonLabel }}
        </button>
      </div>
    </div>

    <div v-if="currentExportJob" class="export-status-card">
      <div class="export-status-header">
        <div>
          <span class="status-pill" :class="statusClass(currentExportJob.status)">
            {{ statusLabel(currentExportJob.status) }}
          </span>
          <span v-if="currentExportJob.stage" class="stage-pill">
            {{ stageLabel(currentExportJob.stage) }}
          </span>
        </div>
        <div class="export-actions">
          <button class="btn btn-sm btn-outline-secondary" type="button" @click="copyDiagnostics">
            Copy Diagnostics
          </button>
          <button
            v-if="currentExportJob.status === 'completed'"
            class="btn btn-sm btn-outline-primary"
            type="button"
            @click="downloadCompletedExport(currentExportJob)"
          >
            Download ZIP
          </button>
        </div>
      </div>

      <p class="export-status-message">
        {{ currentExportJob.status_message || fallbackStatusMessage(currentExportJob) }}
      </p>

      <div v-if="currentExportJob.reused" class="alert alert-info inline-alert">
        An existing export for this month is already active. Tracking that job instead of starting another one.
      </div>

      <div class="export-meta-grid">
        <span><strong>Job ID:</strong> {{ currentExportJob.id }}</span>
        <span><strong>Month:</strong> {{ currentExportJob.month }}</span>
        <span><strong>Started:</strong> {{ formatDate(currentExportJob.started_at) }}</span>
        <span><strong>Updated:</strong> {{ formatDate(currentExportJob.updated_at) }}</span>
        <span><strong>Finished:</strong> {{ formatDate(currentExportJob.finished_at) }}</span>
        <span><strong>Error Code:</strong> {{ currentExportJob.error_code || '-' }}</span>
      </div>

      <details v-if="currentExportJob.error_detail" class="export-details">
        <summary>Technical details</summary>
        <pre>{{ currentExportJob.error_detail }}</pre>
      </details>
    </div>

    <div class="report-meta" v-if="meta">
      <span><strong>Month:</strong> {{ meta.month }}</span>
      <span><strong>Rows:</strong> {{ rows.length }}</span>
      <span><strong>Range:</strong> {{ meta.from }} - {{ meta.to }}</span>
    </div>

    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-if="loading" class="loading-state">
      Loading report data...
    </div>

    <div v-else-if="rows.length === 0" class="empty-state">
      No report rows found for selected month.
    </div>

    <div v-else class="table-container">
      <table class="report-table">
        <thead>
          <tr>
            <th>Transaction Type</th>
            <th>Shop Name</th>
            <th>Order Number</th>
            <th>Order/Cancellation Date</th>
            <th>Invoice Number / Cancellation Invoice</th>
            <th>Customer Name</th>
            <th>Shipping Country</th>
            <th>Customer Type</th>
            <th>VAT ID</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in rows" :key="`${row.transaction_type}-${row.invoice_number}-${idx}`">
            <td>
              <span :class="['badge', row.transaction_type === 'order' ? 'badge-info' : 'badge-warning']">
                {{ row.transaction_type === 'order' ? 'Order' : 'Cancellation' }}
              </span>
            </td>
            <td>{{ row.shop_name }}</td>
            <td>{{ row.order_number }}</td>
            <td>{{ formatDate(row.transaction_date) }}</td>
            <td>{{ row.invoice_number }}</td>
            <td>{{ row.customer_name }}</td>
            <td>{{ row.shipping_country || '-' }}</td>
            <td>{{ formatCustomerType(row.customer_type) }}</td>
            <td>{{ row.customer_vat_id || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import api from '@/utils/api'

const EXPORT_POLL_INTERVAL_MS = 3000
const EXPORT_POLL_TIMEOUT_MS = 30 * 60 * 1000
const EXPORT_STORAGE_KEY = 'billbee-datev-export-job'

export default {
  name: 'DatevReport',
  data() {
    return {
      selectedMonth: this.getDefaultMonth(),
      rows: [],
      meta: null,
      loading: false,
      exportingCsv: false,
      error: null,
      currentExportJob: null,
      pollingJobId: null
    }
  },
  computed: {
    exportButtonLabel() {
      if (!this.exportingCsv) {
        return 'Download DATEV ZIP'
      }

      if (this.currentExportJob?.status === 'queued') {
        return 'Starting Export...'
      }

      return 'Exporting...'
    }
  },
  async mounted() {
    await this.resumeTrackedExport()
    await this.loadReport()
  },
  beforeUnmount() {
    this.pollingJobId = null
  },
  methods: {
    getDefaultMonth() {
      const d = new Date()
      d.setDate(1)
      d.setMonth(d.getMonth() - 1)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      return `${year}-${month}`
    },
    async loadReport() {
      this.loading = true
      this.error = null
      try {
        const response = await api.get('/api/endpoints/datev-report.php', {
          params: {
            month: this.selectedMonth
          }
        })

        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Failed to load DATEV report')
        }

        this.rows = response.data.data.rows || []
        this.meta = {
          month: response.data.data.month,
          from: response.data.data.from,
          to: response.data.data.to
        }
      } catch (err) {
        this.error = err?.response?.data?.error || err.message || 'Unknown error while loading report'
        this.rows = []
        this.meta = null
      } finally {
        this.loading = false
      }
    },
    async downloadDatevZip() {
      this.error = null
      this.exportingCsv = true

      try {
        const startResponse = await api.get('/api/endpoints/datev-export.php', {
          params: {
            action: 'start',
            month: this.selectedMonth
          }
        })

        const job = startResponse.data?.data
        if (!startResponse.data?.success || !job?.id) {
          throw new Error(startResponse.data?.error || 'Failed to start DATEV export')
        }

        this.applyExportJob(job)

        if (job.status === 'failed') {
          throw new Error(this.buildJobErrorMessage(job))
        }

        if (job.status === 'completed') {
          await this.downloadCompletedExport(job)
          return
        }

        await this.pollExportJob(job.id, { autoDownloadOnComplete: true })
      } catch (err) {
        this.error = err?.message || 'Failed to export DATEV ZIP'
        this.exportingCsv = false
      }
    },
    async resumeTrackedExport() {
      const tracked = this.readTrackedJob()
      if (!tracked?.id) {
        return
      }

      if (tracked.month) {
        this.selectedMonth = tracked.month
      }

      try {
        const job = await this.fetchExportStatus(tracked.id)
        if (!job) {
          this.clearTrackedJob()
          return
        }

        this.applyExportJob(job)
        if (job.status === 'queued' || job.status === 'running') {
          this.exportingCsv = true
          await this.pollExportJob(job.id, { autoDownloadOnComplete: false, initialDelay: 0 })
        }
      } catch (err) {
        this.error = err?.message || 'Failed to resume DATEV export status'
        this.exportingCsv = false
      }
    },
    async pollExportJob(jobId, options = {}) {
      const {
        autoDownloadOnComplete = false,
        initialDelay = EXPORT_POLL_INTERVAL_MS
      } = options

      const startedAt = Date.now()
      let delay = initialDelay
      this.pollingJobId = jobId

      while (this.pollingJobId === jobId && Date.now() - startedAt < EXPORT_POLL_TIMEOUT_MS) {
        if (delay > 0) {
          await this.sleep(delay)
        }
        delay = EXPORT_POLL_INTERVAL_MS

        const job = await this.fetchExportStatus(jobId)
        if (!job) {
          throw new Error('The export job could not be found anymore.')
        }

        this.applyExportJob(job)

        if (job.status === 'completed') {
          this.exportingCsv = false
          if (autoDownloadOnComplete) {
            await this.downloadCompletedExport(job)
          }
          return
        }

        if (job.status === 'failed') {
          this.exportingCsv = false
          throw new Error(this.buildJobErrorMessage(job))
        }
      }

      this.exportingCsv = false
      this.error = 'The export is taking longer than expected. The latest job status is still shown below.'
    },
    async fetchExportStatus(jobId) {
      const response = await api.get('/api/endpoints/datev-export.php', {
        params: {
          action: 'status',
          job_id: jobId
        }
      })

      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Failed to check DATEV export status')
      }

      return response.data.data || null
    },
    async downloadCompletedExport(job) {
      const params = new URLSearchParams({
        action: 'download',
        job_id: job.id
      })

      const response = await api.get(`/api/endpoints/datev-export.php?${params.toString()}`, {
        responseType: 'blob'
      })

      const fileName = job.file_name || `datev-export-${job.month}.zip`
      const blobUrl = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      link.target = '_self'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    },
    applyExportJob(job) {
      this.currentExportJob = job
      this.persistTrackedJob(job)
    },
    persistTrackedJob(job) {
      try {
        window.localStorage.setItem(EXPORT_STORAGE_KEY, JSON.stringify({
          id: job.id,
          month: job.month
        }))
      } catch (err) {
        console.warn('Failed to persist DATEV export job', err)
      }
    },
    readTrackedJob() {
      try {
        const raw = window.localStorage.getItem(EXPORT_STORAGE_KEY)
        return raw ? JSON.parse(raw) : null
      } catch (err) {
        console.warn('Failed to read persisted DATEV export job', err)
        return null
      }
    },
    clearTrackedJob() {
      try {
        window.localStorage.removeItem(EXPORT_STORAGE_KEY)
      } catch (err) {
        console.warn('Failed to clear persisted DATEV export job', err)
      }
    },
    buildJobErrorMessage(job) {
      if (job?.error) {
        return `${job.error} Support reference: ${job.id}.`
      }

      return `DATEV export failed. Support reference: ${job?.id || 'unknown'}.`
    },
    fallbackStatusMessage(job) {
      const messages = {
        queued: 'The export is queued and waiting for the worker to confirm startup.',
        running: 'The export is currently running.',
        completed: 'The DATEV ZIP archive is ready.',
        failed: 'The DATEV export failed.'
      }

      return messages[job?.status] || 'Tracking DATEV export status.'
    },
    statusLabel(status) {
      return {
        queued: 'Queued',
        running: 'Running',
        completed: 'Completed',
        failed: 'Failed'
      }[status] || status
    },
    stageLabel(stage) {
      return {
        queued: 'Queued',
        starting: 'Starting Worker',
        generating: 'Generating ZIP',
        finalizing: 'Finalizing',
        completed: 'Ready',
        failed: 'Failed'
      }[stage] || stage
    },
    statusClass(status) {
      return {
        queued: 'status-queued',
        running: 'status-running',
        completed: 'status-completed',
        failed: 'status-failed'
      }[status] || 'status-queued'
    },
    async copyDiagnostics() {
      if (!this.currentExportJob) {
        return
      }

      const diagnostics = [
        `job_id=${this.currentExportJob.id}`,
        `month=${this.currentExportJob.month}`,
        `status=${this.currentExportJob.status}`,
        `stage=${this.currentExportJob.stage || '-'}`,
        `message=${this.currentExportJob.status_message || '-'}`,
        `error_code=${this.currentExportJob.error_code || '-'}`,
        `started_at=${this.currentExportJob.started_at || '-'}`,
        `updated_at=${this.currentExportJob.updated_at || '-'}`,
        `finished_at=${this.currentExportJob.finished_at || '-'}`,
        `error_detail=${this.currentExportJob.error_detail || '-'}`
      ].join('\n')

      try {
        await navigator.clipboard.writeText(diagnostics)
      } catch (err) {
        console.warn('Failed to copy DATEV export diagnostics', err)
      }
    },
    sleep(ms) {
      return new Promise(resolve => window.setTimeout(resolve, ms))
    },
    formatDate(value) {
      if (!value) return 'N/A'
      return new Date(value).toLocaleString('de-DE')
    },
    formatCustomerType(value) {
      if (value === 'business') return 'Business'
      if (value === 'private') return 'Private'
      return '-'
    }
  }
}
</script>

<style scoped>
.datev-report-page {
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.control-label {
  font-weight: 600;
}

.month-input {
  padding: 0.4rem 0.6rem;
  border: 1px solid #ccc;
  border-radius: 6px;
}

.export-status-card {
  margin-bottom: 1rem;
  padding: 1rem;
  background: #fff;
  border: 1px solid #dbe4f0;
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
}

.export-status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.export-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.status-pill,
.stage-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.stage-pill {
  margin-left: 0.5rem;
  background: #eef2ff;
  color: #4338ca;
}

.status-queued {
  background: #fef3c7;
  color: #92400e;
}

.status-running {
  background: #dbeafe;
  color: #1d4ed8;
}

.status-completed {
  background: #dcfce7;
  color: #166534;
}

.status-failed {
  background: #fee2e2;
  color: #991b1b;
}

.export-status-message {
  margin: 0.85rem 0;
  color: #1f2937;
}

.inline-alert {
  margin-bottom: 0.85rem;
}

.export-meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
  color: #374151;
}

.export-details {
  margin-top: 1rem;
}

.export-details summary {
  cursor: pointer;
  font-weight: 600;
}

.export-details pre {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: #111827;
  color: #f9fafb;
  border-radius: 8px;
  white-space: pre-wrap;
  word-break: break-word;
}

.report-meta {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;
  color: #374151;
  flex-wrap: wrap;
}

.loading-state,
.empty-state {
  padding: 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.table-container {
  overflow-x: auto;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
}

.report-table th,
.report-table td {
  padding: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
}

.report-table th {
  background: #f3f4f6;
  font-weight: 700;
}

.badge {
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.badge-info {
  background: #dbeafe;
  color: #1e40af;
}

.badge-warning {
  background: #fef3c7;
  color: #92400e;
}

.alert {
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
}

.alert-danger {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.alert-info {
  background: #e0f2fe;
  color: #075985;
  border: 1px solid #bae6fd;
}
</style>
